import { useMemo } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';

import { LOCALES, OMITTED_EDITORIAL_LOCALES } from 'localization/constants/configEnums';
import { t } from 'localization/utils/t';
import { useCanonical } from 'layout/canonical/useCanonical';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';

const TP = 'layout.head.GlobalMetadata';

export function GlobalMetadata() {
  const canonical = useCanonical();
  const { pageTitle } = usePageMetadata({
    defaultTitle: t(`${TP}.title`, `Buy and Sell Authentic Sneakers, Apparel, and Accessories `),
  });

  const { asPath, locales } = useRouter();
  const path = asPath?.split(/[#?]/)?.[0];
  const baseUrl = 'https://www.goat.com';
  const enUSUrl = `${baseUrl}${path}`;

  /* Create Alternate Links for all locales */
  const hreflangs = useMemo(() => {
    return locales.map((locale) => {
      let href = enUSUrl;
      if (OMITTED_EDITORIAL_LOCALES.includes(locale as LOCALES) && path.includes('editorial')) {
        return null;
      }
      if (locale !== LOCALES.enUS) {
        href = path === '/' ? `${baseUrl}/${locale}` : `${baseUrl}/${locale}${path}`;
      }
      return <link key={locale} rel="alternate" hrefLang={locale} href={href} />;
    });
  }, [locales, path, enUSUrl]);

  return (
    <Head>
      <link
        rel="apple-touch-icon"
        href="/images/icons/apple-touch-icon.png"
        key="apple-touch-icon"
      />
      <link rel="icon" href="/favicon.ico" key="favicon" />
      <link rel="manifest" href="/manifest.json" key="manifest" />
      <meta
        name="description"
        content={t(
          `${TP}.siteDescription`,
          `The global platform for the greatest products from the past, present and future. Shop new and used sneakers, apparel and accessories.`,
        )}
        key="description"
      />
      <meta
        property="og:description"
        content={t(
          `${TP}.siteDescription`,
          `The global platform for the greatest products from the past, present and future. Shop new and used sneakers, apparel and accessories.`,
        )}
        key="og:description"
      />
      <meta name="theme-color" content="#000" key="theme-color" />
      <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0 maximum-scale=1.0"
        key="viewport"
      />
      <meta property="og:image" content="/images/icons/goat-logo-512.png" key="og:image" />
      <title key="title">{pageTitle}</title>
      {hreflangs}
      <link rel="alternate" hrefLang="x-default" href={enUSUrl} />
      <link rel="canonical" href={canonical} />
    </Head>
  );
}
